import { Controller } from "@hotwired/stimulus";

// Reveals the fields that the document-upload AI populated: each matched field
// gets a staggered highlight pulse and a small "AI" sparkle badge on its label.
// Purely cosmetic — the values are already rendered server-side; this just makes
// the extraction visible. Driven by the `fields` value emitted in jobs/_form.
export default class extends Controller {
  static values = {
    fields: Object,
    badge: String,
    stagger: { type: Number, default: 250 },
  };

  connect() {
    // Defer so the form (and any TipTap editors) are fully laid out first.
    this.timeouts = [];
    requestAnimationFrame(() => this.reveal());
  }

  disconnect() {
    this.timeouts.forEach((t) => clearTimeout(t));
  }

  reveal() {
    const targets = this.collectTargets();
    if (targets.length === 0) return;

    targets.forEach((target, index) => {
      const timeout = setTimeout(() => {
        this.decorate(target);
        if (index === 0) {
          target.pulse.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, index * this.staggerValue);
      this.timeouts.push(timeout);
    });
  }

  // Resolve the logical field map into ordered, de-duplicated reveal targets.
  // Each target is { pulse, anchor }: `pulse` is the element to flash, `anchor`
  // is where the badge is appended.
  collectTargets() {
    const fields = this.fieldsValue || {};
    const targets = [];

    (fields.attributes || []).forEach((name) => {
      // The daily/hourly/fixed rate forms all render identical field names, but
      // only the selected one is visible — badge the visible group, not the
      // first match (which may be in a display:none container).
      const inputs = this.element.querySelectorAll(
        `[name="job[${name}]"], [name="${name}"]`,
      );
      this.pushField(targets, this.visibleGroup(inputs));
    });

    // Both the template-driven scoped fields (hidden `[key]` input in a
    // .form-group) and the advanced key/value rows (visible `[key]` input in a
    // .nested-fields row) carry the field key in an input whose name ends `[key]`.
    (fields.custom_field_keys || []).forEach((key) => {
      const keyInput = Array.from(
        this.element.querySelectorAll('input[name$="[key]"]'),
      ).find((input) => input.value === key);
      if (keyInput) this.pushField(targets, this.groupFor(keyInput));
    });

    // Only badge the "Suggested Supplier" typeahead when an existing supplier was
    // matched; unmatched suppliers surface as external rows, badged separately.
    if (fields.present_supplier_count > 0) {
      this.pushField(
        targets,
        this.groupFor(this.element.querySelector(".multiple-suppliers-typeahead")),
      );
    }
    if (fields.external_supplier_count > 0) {
      // The hidden insertion template also carries an .es-title, so skip anything
      // that isn't actually visible.
      Array.from(this.element.querySelectorAll(".no-supplier-entry .es-title"))
        .filter((el) => el.offsetParent !== null)
        .forEach((el) => this.pushField(targets, this.groupFor(el)));
    }

    // Badge each AI-suggested milestone next to its "Milestone N" heading.
    if (fields.milestone_count > 0) {
      Array.from(this.element.querySelectorAll(".milestone-row"))
        .filter((row) => row.offsetParent !== null)
        .slice(0, fields.milestone_count)
        .forEach((row) => {
          targets.push({ pulse: row, anchor: row.querySelector("h5") });
        });
    }

    return this.dedupe(targets);
  }

  pushField(targets, group) {
    if (!group) return;
    targets.push({ pulse: group, anchor: group.querySelector("label") || group });
  }

  groupFor(el) {
    if (!el) return null;
    return el.closest(".form-group, .nested-fields, td") || el;
  }

  // From a NodeList of same-named inputs, return the containing group that is
  // actually visible (falling back to the first if none are).
  visibleGroup(inputs) {
    const groups = Array.from(inputs)
      .map((el) => this.groupFor(el))
      .filter(Boolean);
    return groups.find((group) => group.offsetParent !== null) || groups[0] || null;
  }

  dedupe(targets) {
    const seen = new Set();
    return targets
      .filter(({ pulse }) => {
        if (!pulse || seen.has(pulse)) return false;
        seen.add(pulse);
        return true;
      })
      .sort((a, b) => {
        const pos = a.pulse.compareDocumentPosition(b.pulse);
        if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });
  }

  decorate({ pulse, anchor }) {
    pulse.classList.add("ai-reveal-pulse");
    setTimeout(() => pulse.classList.remove("ai-reveal-pulse"), 1600);

    if (!anchor || anchor.querySelector(".ai-reveal-badge")) return;

    // Star icon is supplied by CSS (.ai-reveal-badge::before); keep the node text-only.
    const badge = document.createElement("span");
    badge.className = "ai-reveal-badge";
    badge.textContent = this.hasBadgeValue
      ? this.badgeValue
      : window.I18n["jobs.form.document_upload.ai_filled_badge"];
    anchor.appendChild(badge);
  }
}
